import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Briefcase, Copy, Send, CreditCard, Inbox, Loader2, X, ChevronRight } from "lucide-react";

const COLUMNS = [
  { key: "pending",     title: "Nouveaux briefs",        color: "#9CA3AF" },
  { key: "accepted",    title: "Acceptés",                color: "#FBBF24" },
  { key: "in_progress", title: "En production",          color: "#D4AF37" },
  { key: "delivered",   title: "Livrés (à payer)",       color: "#10B981" },
  { key: "paid",        title: "Payés",                    color: "#10B981" },
];

export default function AgencyDashboard() {
  const { user } = useAuth();
  const [briefs, setBriefs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({ by_status: {}, paid_revenue_eur: 0 });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const [b, s, p] = await Promise.all([
        api.get("/agency/briefs"),
        api.get("/agency/briefs/stats"),
        api.get("/projects"),
      ]);
      setBriefs(b.data);
      setStats(s.data);
      setProjects(p.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const briefLink = user ? `${window.location.origin}/brief/${user.user_id}` : "";

  const copyLink = () => {
    navigator.clipboard.writeText(briefLink);
    toast.success("Lien copié — partagez-le à vos clients");
  };

  const groupedBriefs = COLUMNS.reduce((acc, col) => {
    acc[col.key] = briefs.filter(b => b.status === col.key);
    return acc;
  }, {});

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="label-mono">Mode Agence</span>
          <h1 className="mt-2 font-heading text-4xl tracking-tighter">
            <span className="gold-text">Briefs clients</span> & livraisons
          </h1>
        </div>
        <div className="text-right">
          <div className="label-mono">Chiffre encaissé</div>
          <div className="text-3xl font-heading gold-text font-semibold mt-1" data-testid="agency-revenue">{stats.paid_revenue_eur.toFixed(0)} €</div>
        </div>
      </div>

      {/* Public brief link */}
      <div className="gold-border-gradient p-5 mb-8 flex flex-wrap items-center gap-4">
        <Briefcase className="w-6 h-6 text-gold" />
        <div className="flex-1 min-w-[240px]">
          <div className="label-mono">Votre lien de brief public</div>
          <div className="font-mono text-xs text-gold mt-1 break-all" data-testid="brief-public-link">{briefLink}</div>
        </div>
        <button onClick={copyLink} data-testid="copy-brief-link" className="ghost-btn !py-2 !px-4 text-sm">
          <Copy className="w-4 h-4" /> Copier
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        {COLUMNS.map(col => (
          <div key={col.key} className="studio-card p-4">
            <div className="text-xs uppercase tracking-widest text-white/50">{col.title}</div>
            <div className="mt-1 text-3xl font-heading" style={{color: col.color}}>{(stats.by_status[col.key]?.count) || 0}</div>
          </div>
        ))}
      </div>

      {/* Kanban */}
      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-8 h-8 text-gold animate-spin mx-auto" /></div>
      ) : briefs.length === 0 ? (
        <div className="gold-border-gradient p-12 text-center">
          <Inbox className="w-12 h-12 text-gold mx-auto" />
          <h3 className="mt-4 font-heading text-2xl">Aucun brief pour l'instant</h3>
          <p className="text-white/55 mt-2">Partagez votre lien public pour recevoir vos premiers briefs clients.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          {COLUMNS.map(col => (
            <div key={col.key} className="space-y-3">
              <div className="flex items-center justify-between px-2">
                <span className="label-mono" style={{color: col.color}}>{col.title}</span>
                <span className="text-xs text-white/40">{groupedBriefs[col.key].length}</span>
              </div>
              <div className="space-y-2 max-h-[70vh] overflow-y-auto scroll-thin pr-1">
                {groupedBriefs[col.key].map(b => (
                  <button key={b.brief_id} onClick={() => setSelected(b)} data-testid={`brief-card-${b.brief_id}`}
                    className="w-full text-left studio-card p-3 hover:border-gold/40 transition">
                    <div className="flex items-center justify-between text-xs">
                      <span className="capitalize text-gold/80">{b.request_type}</span>
                      {b.price && <span className="font-mono text-gold">{b.price}€</span>}
                    </div>
                    <div className="mt-2 text-sm font-heading truncate">{b.client.name}</div>
                    <div className="text-[11px] text-white/45 truncate">{b.client.email}</div>
                    <div className="mt-2 text-xs text-white/70 line-clamp-2">{b.prompt}</div>
                  </button>
                ))}
                {groupedBriefs[col.key].length === 0 && <div className="text-xs text-white/30 italic px-2">—</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <BriefDrawer brief={selected} projects={projects} onClose={() => setSelected(null)} onRefresh={refresh} />
      )}
    </div>
  );
}

function BriefDrawer({ brief: initial, projects, onClose, onRefresh }) {
  const [brief, setBrief] = useState(initial);
  const [price, setPrice] = useState(initial.price || initial.budget_estimate || "");
  const [notes, setNotes] = useState(initial.admin_notes || "");
  const [deliverableId, setDeliverableId] = useState(initial.deliverable_project_id || "");
  const [loading, setLoading] = useState(false);

  const update = async (patch) => {
    setLoading(true);
    try {
      const { data } = await api.patch(`/agency/briefs/${brief.brief_id}`, patch);
      setBrief(data);
      onRefresh();
      toast.success("Brief mis à jour");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const sendQuote = async () => {
    if (!deliverableId) { toast.error("Choisissez un livrable d'abord"); return; }
    if (!price) { toast.error("Indiquez un prix"); return; }
    setLoading(true);
    try {
      // Make sure deliverable + price are saved first
      await api.patch(`/agency/briefs/${brief.brief_id}`, {
        deliverable_project_id: deliverableId,
        price: parseFloat(price),
      });
      const { data } = await api.post(`/agency/briefs/${brief.brief_id}/quote`, { price: parseFloat(price) });
      toast.success(`Devis envoyé. Lien client: ${data.tracking_url}`);
      onRefresh();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full sm:w-[520px] bg-obsidian border-l border-gold/30 overflow-y-auto scroll-thin" onClick={(e)=>e.stopPropagation()}>
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <h3 className="font-heading text-xl">Brief #{brief.brief_id.slice(-8)}</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/5 rounded"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Client */}
          <div>
            <div className="label-mono mb-2">Client</div>
            <div className="space-y-1 text-sm">
              <div>{brief.client.name} <span className="text-white/40">— {brief.client.email}</span></div>
              {brief.client.company && <div className="text-white/60 text-xs">{brief.client.company}</div>}
              {brief.client.phone && <div className="text-white/60 text-xs">{brief.client.phone}</div>}
            </div>
          </div>

          {/* Request */}
          <div>
            <div className="label-mono mb-2">Demande ({brief.request_type})</div>
            <div className="text-sm text-white/85 p-3 bg-obsidian-light rounded border border-white/10">{brief.prompt}</div>
            {brief.details && <div className="mt-2 text-xs text-white/55 p-3 bg-obsidian-light rounded">{brief.details}</div>}
            {brief.budget_estimate && <div className="mt-2 text-xs text-white/60">Budget annoncé : {brief.budget_estimate}€</div>}
            {brief.deadline && <div className="text-xs text-white/60">Deadline : {brief.deadline}</div>}
          </div>

          {/* Status workflow */}
          <div>
            <div className="label-mono mb-2">Statut</div>
            <div className="grid grid-cols-3 gap-2">
              {["accepted", "in_progress", "cancelled"].map(s => (
                <button key={s} data-testid={`status-${s}`} onClick={()=>update({status: s})} disabled={loading}
                  className={`py-2 rounded text-xs ${brief.status === s ? "bg-gold text-black" : "bg-white/5 hover:bg-white/10"}`}>
                  {s.replace("_", " ")}
                </button>
              ))}
            </div>
          </div>

          {/* Deliverable */}
          <div>
            <div className="label-mono mb-2">Livrable (depuis vos projets)</div>
            <select data-testid="deliverable-select" value={deliverableId} onChange={(e)=>setDeliverableId(e.target.value)}
              className="w-full bg-obsidian-light border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-gold/50">
              <option value="">— Aucun —</option>
              {projects.map(p => (
                <option key={p.project_id} value={p.project_id}>{p.kind.toUpperCase()} · {p.title?.slice(0, 50)}</option>
              ))}
            </select>
          </div>

          {/* Price */}
          <div>
            <div className="label-mono mb-2">Prix de la prestation (€)</div>
            <input data-testid="brief-price" type="number" step="10" min="0" value={price} onChange={(e)=>setPrice(e.target.value)}
              className="w-full bg-obsidian-light border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-gold/50" />
          </div>

          {/* Notes */}
          <div>
            <div className="label-mono mb-2">Notes internes</div>
            <textarea data-testid="brief-notes" value={notes} onChange={(e)=>setNotes(e.target.value)} onBlur={()=>update({admin_notes: notes})}
              className="w-full h-20 bg-obsidian-light border border-white/10 rounded-lg p-3 text-sm outline-none focus:border-gold/50 resize-none"
              placeholder="Notes privées sur ce brief..." />
          </div>

          {brief.payment_url && (
            <div className="p-3 bg-gold/5 border border-gold/20 rounded text-xs">
              <div className="label-mono mb-1">Lien de paiement client</div>
              <div className="font-mono text-gold break-all">{brief.payment_url.slice(0, 80)}...</div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-3 border-t border-white/10">
            <button data-testid="send-quote-btn" onClick={sendQuote} disabled={loading || !deliverableId || !price}
              className="gold-btn flex-1 !py-3 disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Envoyer devis & livrable</>}
            </button>
          </div>

          <a href={`/brief/track/${brief.access_token}`} target="_blank" rel="noreferrer" className="text-xs text-gold/80 hover:text-gold flex items-center gap-1 justify-center pt-2">
            Voir la page client <ChevronRight className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
}
